import type { DocumentLayout } from './document-page-layout';
import { pageDimensions } from './document-page-layout';
import { safeEditorImage, safeEditorLink } from './document-editor-tools';

/** CSS string escaping, independent of HTML; arbitrary document text cannot become a CSS rule. */
function cssString(value: string) {
  return '"' + Array.from(value, character => '\\' + character.codePointAt(0)!.toString(16) + ' ').join('') + '"';
}
function regionContent(nodes: DocumentLayout['header'], title: string): string {
  const parts: string[] = [];
  const visit = (node: DocumentLayout['header'][number]) => {
    if (node.type === 'layoutToken') parts.push(node.attrs?.kind === 'pageNumber' ? 'counter(page)' : cssString(title));
    else if (node.type === 'text') parts.push(cssString(node.text ?? ''));
    else if (node.type === 'hardBreak') parts.push(cssString(' '));
    else { node.content?.forEach(visit); if (node.type === 'paragraph') parts.push(cssString(' ')); }
  };
  nodes.forEach(visit);
  return parts.join(' ') || 'none';
}
export function documentPrintCss(layout: DocumentLayout, title: string) {
  const { width, height } = pageDimensions(layout.pageSettings);
  const { top, right, bottom, left } = layout.pageSettings.margins;
  if (![top, right, bottom, left].every(value => Number.isFinite(value) && value >= 5 && value <= 50)) throw new Error('Invalid page margins.');
  return `@page { size: ${width}mm ${height}mm; margin: ${top}mm ${right}mm ${bottom}mm ${left}mm;
    @top-center { content: ${regionContent(layout.header, title)}; font: 10pt sans-serif; white-space: pre-wrap; }
    @bottom-center { content: ${regionContent(layout.footer, title)}; font: 10pt sans-serif; white-space: pre-wrap; }
  }
  body { margin: 0; color: black; background: white; font: 11pt/1.5 sans-serif; overflow-wrap: anywhere; }
  img { max-width: 100%; height: auto; } table { border-collapse: collapse; width: 100%; }
  td, th { border: 1px solid; padding: 4pt; } th { background: #eee; }
  h1, h2, h3 { break-after: avoid; } blockquote { border-left: 2px solid; padding-left: 12pt; }
  [data-page-break] { break-after: page; height: 0; margin: 0; border: 0; }
  [data-page-break]::after { content: none; }
  [data-table-of-contents] { border: 1px solid; padding: 10pt; }
  [data-heading-level="2"] { padding-left: 12pt; } [data-heading-level="3"] { padding-left: 24pt; }`;
}

/** Copy the rendered editor only, never app chrome, action controls or unrelated private panels. */
export function populateDocumentPrint(target: Document, source: HTMLElement, layout: DocumentLayout, title: string) {
  target.title = title;
  const style = target.createElement('style'); style.textContent = documentPrintCss(layout, title); target.head.append(style);
  const body = source.cloneNode(true) as HTMLElement;
  body.querySelectorAll('[data-section-marker], [data-section-boundary], [data-section-title-id]').forEach(node => node.remove());
  body.querySelectorAll('button').forEach(button => {
    if (button.closest('nav[aria-label="Document table of contents"]')) {
      const entry = target.createElement('div'); entry.textContent = button.textContent;
      entry.dataset.headingLevel = button.dataset.headingLevel; button.replaceWith(entry);
    } else button.remove();
  });
  body.querySelectorAll('script,style,iframe,object,embed,input,textarea,select,form').forEach(node => node.remove());
  for (const element of [body, ...Array.from(body.querySelectorAll<HTMLElement>('*'))]) {
    const align = element.style.textAlign;
    const presentation: Partial<CSSStyleDeclaration> = {};
    for (const key of ['fontFamily','fontSize','color','fontWeight','fontStyle','textDecoration','marginTop','marginBottom','lineHeight'] as const) {
      const value = element.style[key];
      if (value && !/[{}<>]|url\s*\(|expression/i.test(value)) presentation[key] = value;
    }
    const width = element instanceof HTMLImageElement ? element.width : undefined;
    const height = element instanceof HTMLImageElement ? element.height : undefined;
    for (const attribute of Array.from(element.attributes)) {
      if (!['data-page-break', 'data-table-of-contents', 'data-heading-level', 'colspan', 'rowspan', 'alt'].includes(attribute.name)) element.removeAttribute(attribute.name);
    }
    Object.assign(element.style, presentation);
    if (element.tagName === 'IMG') { if (width && width <= 4096) element.setAttribute('width',String(width)); if (height && height <= 4096) element.setAttribute('height',String(height)); }
    if (['left', 'right', 'center', 'justify'].includes(align)) element.style.textAlign = align;
  }
  // Resource addresses come from the original rendered tree, rechecked before copying.
  const originalImages = source.querySelectorAll('img');
  body.querySelectorAll('img').forEach((img, index) => {
    const original = originalImages[index];
    const value = original?.getAttribute('src') ?? '';
    const src = value.startsWith('blob:') && original?.closest('[data-document-image]') ? value : safeEditorImage(value);
    if (src) img.src = src; else img.replaceWith(target.createTextNode(img.alt));
  });
  const originalLinks = source.querySelectorAll('a');
  body.querySelectorAll('a').forEach((link, index) => {
    const href = safeEditorLink(originalLinks[index]?.getAttribute('href') ?? '');
    if (href) link.href = href;
  });
  target.body.replaceChildren(body);
}
export async function printDocument(source: HTMLElement, layout: DocumentLayout, title: string, signal: AbortSignal) {
  const frame = document.createElement('iframe');
  frame.title = 'Document print preview';
  frame.className = 'fixed bottom-0 left-0 h-0 w-0 border-0';
  document.body.append(frame);
  const dispose = () => frame.remove();
  signal.addEventListener('abort', dispose, { once: true });
  try {
    if (!frame.contentDocument || !frame.contentWindow) throw new Error('Print preview could not be opened.');
    populateDocumentPrint(frame.contentDocument, source, layout, title);
    const images = Array.from(frame.contentDocument.images);
    await Promise.race([
      Promise.all(images.map(img => img.decode?.().catch(() => undefined))),
      new Promise<void>(resolve => window.setTimeout(resolve, 5000))
    ]);
    if (signal.aborted) return;
    frame.contentWindow.addEventListener('afterprint', dispose, { once: true });
    frame.contentWindow.focus(); frame.contentWindow.print();
  } catch (error) { dispose(); throw error; }
}
